import React, { useState, useMemo } from 'react';
import {
  Shield,
  AlertTriangle,
  Filter,
  Search,
  Plus,
  Edit,
  Trash2,
  CheckCircle2,
  Info,
  Lock,
  Unlock,
  BarChart2,
  TrendingUp,
  TrendingDown
} from 'lucide-react';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';

// Type Definitions
interface RiskScore {
  id: string;
  userId: string;
  userName: string;
  overallRiskScore: number;
  riskFactors: {
    transactionVolume: number;
    accountAge: number;
    geographicRisk: number;
    suspiciousActivity: number;
  };
  riskLevel: 'low' | 'medium' | 'high';
}

interface FraudDetectionRule {
  id: string;
  name: string;
  description: string;
  category: 'transaction' | 'account' | 'behavior';
  conditions: string[];
  action: 'flag' | 'block' | 'review';
  severity: 'low' | 'medium' | 'high';
}

interface TransactionLimit {
  id: string;
  type: 'daily' | 'single' | 'monthly';
  amount: number;
  currency: string;
  appliesTo: 'all' | 'new_users' | 'high_risk_users';
}

interface BlacklistEntry {
  id: string;
  type: 'user' | 'account' | 'ip' | 'device';
  identifier: string;
  reason: string;
  addedBy: string;
  addedAt: string;
}

interface SecurityAlert {
  id: string;
  type: 'suspicious_activity' | 'potential_fraud' | 'anomaly_detected';
  severity: 'low' | 'medium' | 'high';
  description: string;
  timestamp: string;
  status: 'new' | 'investigated' | 'resolved';
}

const RiskManagement: React.FC = () => {
  useBreadcrumbs([
    { label: 'Escrow Management', href: '#' },
    { label: 'Escrow Risk Management' }
  ]);
  // State Management
  const [riskScores, setRiskScores] = useState<RiskScore[]>([
    {
      id: 'RISK001',
      userId: 'USER123',
      userName: 'John Doe',
      overallRiskScore: 65,
      riskFactors: {
        transactionVolume: 20,
        accountAge: 15,
        geographicRisk: 10,
        suspiciousActivity: 20
      },
      riskLevel: 'medium'
    }
  ]);

  const [fraudDetectionRules, setFraudDetectionRules] = useState<FraudDetectionRule[]>([
    {
      id: 'RULE001',
      name: 'Unusual Transaction Pattern',
      description: 'Detect rapid multiple transactions from different locations',
      category: 'transaction',
      conditions: [
        'More than 5 transactions in 1 hour',
        'Transactions from different geographic locations'
      ],
      action: 'review',
      severity: 'high'
    }
  ]);

  const [transactionLimits, setTransactionLimits] = useState<TransactionLimit[]>([
    {
      id: 'LIMIT001',
      type: 'daily',
      amount: 5000,
      currency: 'GHS',
      appliesTo: 'new_users'
    }
  ]);

  const [blacklist, setBlacklist] = useState<BlacklistEntry[]>([
    {
      id: 'BL001',
      type: 'ip',
      identifier: '192.168.1.100',
      reason: 'Suspicious login attempts',
      addedBy: 'system',
      addedAt: '2024-02-20'
    }
  ]);

  const [securityAlerts, setSecurityAlerts] = useState<SecurityAlert[]>([
    {
      id: 'ALERT001',
      type: 'suspicious_activity',
      severity: 'high',
      description: 'Multiple failed login attempts from unknown location',
      timestamp: '2024-02-25 10:30:45',
      status: 'new'
    }
  ]);

  // Filtering States
  const [riskFilter, setRiskFilter] = useState('all');
  const [riskSearch, setRiskSearch] = useState('');

  // Render Risk Scoring
  const renderRiskScoring = () => {
    // Filter Risk Scores
    const filteredRiskScores = riskScores.filter(score => {
      const matchesRiskLevel = riskFilter === 'all' || score.riskLevel === riskFilter;
      const matchesSearch =
        score.userName.toLowerCase().includes(riskSearch.toLowerCase()) ||
        score.userId.toLowerCase().includes(riskSearch.toLowerCase());
      return matchesRiskLevel && matchesSearch;
    });

    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <BarChart2 className="mr-2 h-5 w-5 text-blue-500" />
            Risk Scoring
          </h2>
          <div className="flex items-center space-x-2">
            <div className="relative">
              <input
                type="text"
                placeholder="Search users"
                value={riskSearch}
                onChange={(e) => setRiskSearch(e.target.value)}
                className="pl-8 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <Search className="absolute left-2 top-3 h-4 w-4 text-gray-400" />
            </div>
            <select
              value={riskFilter}
              onChange={(e) => setRiskFilter(e.target.value)}
              className="pl-8 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Levels</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Overall Risk Score
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Risk Factors
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Risk Level
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredRiskScores.map((score) => (
                <tr key={score.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {score.userName}
                        </div>
                        <div className="text-sm text-gray-500">
                          {score.userId}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div
                        className={`w-12 h-12 rounded-full flex items-center justify-center font-bold ${score.overallRiskScore <= 30
                            ? 'bg-green-100 text-green-800'
                            : score.overallRiskScore <= 70
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                      >
                        {score.overallRiskScore}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="space-y-1">
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500">Transaction Volume</span>
                        <span className="text-xs font-medium">{score.riskFactors.transactionVolume}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500">Account Age</span>
                        <span className="text-xs font-medium">{score.riskFactors.accountAge}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500">Geographic Risk</span>
                        <span className="text-xs font-medium">{score.riskFactors.geographicRisk}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500">Suspicious Activity</span>
                        <span className="text-xs font-medium">{score.riskFactors.suspiciousActivity}</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${score.riskLevel === 'low'
                        ? 'bg-green-100 text-green-800'
                        : score.riskLevel === 'medium'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                      {score.riskLevel.charAt(0).toUpperCase() + score.riskLevel.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      className="text-blue-600 hover:text-blue-900"
                      onClick={() => {
                        // TODO: Implement view details logic
                      }}
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Render Fraud Detection Rules
  const renderFraudDetectionRules = () => {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <Shield className="mr-2 h-5 w-5 text-red-500" />
            Fraud Detection Rules
          </h2>
          <button
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
            onClick={() => {
              // TODO: Implement add rule logic
            }}
          >
            <Plus className="mr-2 h-4 w-4" /> New Rule
          </button>
        </div>
        <div className="space-y-4">
          {fraudDetectionRules.map((rule) => (
            <div
              key={rule.id}
              className="bg-gray-50 p-4 rounded-lg border border-gray-200"
            >
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="font-medium text-gray-900">{rule.name}</h3>
                  <p className="text-sm text-gray-500">{rule.description}</p>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${rule.severity === 'high'
                      ? 'bg-red-100 text-red-800'
                      : rule.severity === 'medium'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-green-100 text-green-800'
                    }`}>
                    {rule.severity.charAt(0).toUpperCase() + rule.severity.slice(1)}
                  </span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${rule.action === 'block'
                      ? 'bg-red-100 text-red-800'
                      : rule.action === 'review'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-green-100 text-green-800'
                    }`}>
                    {rule.action.charAt(0).toUpperCase() + rule.action.slice(1)}
                  </span>
                </div>
              </div>
              <div className="mt-2">
                <p className="text-sm text-gray-500">Conditions:</p>
                <ul className="list-disc list-inside text-sm text-gray-700">
                  {rule.conditions.map((condition, index) => (
                    <li key={index}>{condition}</li>
                  ))}
                </ul>
              </div>
              <div className="mt-2 flex justify-end space-x-2">
                <button
                  className="text-blue-600 hover:text-blue-900"
                  onClick={() => {
                    // TODO: Implement edit rule logic
                  }}
                >
                  <Edit className="h-4 w-4 mr-1" /> Edit
                </button>
                <button
                  className="text-red-600 hover:text-red-900"
                  onClick={() => {
                    // TODO: Implement delete rule logic
                  }}
                >
                  <Trash2 className="h-4 w-4 mr-1" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <Shield className="mr-3 h-8 w-8 text-red-600" />
            Risk Management
          </h1>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Risk Scoring */}
            <div>
              {renderRiskScoring()}
            </div>

            {/* Fraud Detection Rules */}
            <div>
              {renderFraudDetectionRules()}
            </div>
          </div>

          {/* Transaction Limits and Blacklist Management */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Transaction Limits */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                  <AlertTriangle className="mr-2 h-5 w-5 text-yellow-500" />
                  Transaction Limits
                </h2>
                <button
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
                  onClick={() => {
                    // TODO: Implement add transaction limit logic
                  }}
                >
                  <Plus className="mr-2 h-4 w-4" /> New Limit
                </button>
              </div>
              <div className="space-y-4">
                {transactionLimits.map((limit) => (
                  <div
                    key={limit.id}
                    className="bg-gray-50 p-4 rounded-lg border border-gray-200"
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <h3 className="font-medium text-gray-900 capitalize">
                          {limit.type} Limit
                        </h3>
                        <p className="text-sm text-gray-500">
                          {limit.appliesTo.replace('_', ' ').charAt(0).toUpperCase() + limit.appliesTo.replace('_', ' ').slice(1)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-blue-600">
                          {limit.currency} {limit.amount.toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <div className="mt-2 flex justify-end space-x-2">
                      <button
                        className="text-blue-600 hover:text-blue-900"
                        onClick={() => {
                          // TODO: Implement edit limit logic
                        }}
                      >
                        <Edit className="h-4 w-4 mr-1" /> Edit
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Blacklist Management */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                  <Lock className="mr-2 h-5 w-5 text-red-500" />
                  Blacklist Management
                </h2>
                <button
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
                  onClick={() => {
                    // TODO: Implement add blacklist entry logic
                  }}
                >
                  <Plus className="mr-2 h-4 w-4" /> Add Entry
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Identifier
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Reason
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Added
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {blacklist.map((entry) => (
                      <tr key={entry.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
                          {entry.type}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {entry.identifier}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {entry.reason}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {entry.addedAt}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                          <button
                            className="text-red-600 hover:text-red-900"
                            onClick={() => {
                              // TODO: Implement remove from blacklist logic
                              setBlacklist(prev =>
                                prev.filter(b => b.id !== entry.id)
                              );
                            }}
                          >
                            <Unlock className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Security Alerts */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <AlertTriangle className="mr-2 h-5 w-5 text-red-500" />
                Security Alerts
              </h2>
              <button
                className="text-blue-600 hover:text-blue-800 flex items-center"
                onClick={() => {
                  // TODO: Implement mark all alerts as resolved
                  setSecurityAlerts(prev =>
                    prev.map(alert => ({
                      ...alert,
                      status: 'resolved'
                    }))
                  );
                }}
              >
                <CheckCircle2 className="mr-1 h-4 w-4" /> Mark All Resolved
              </button>
            </div>
            <div className="space-y-4">
              {securityAlerts.map((alert) => (
                <div
                  key={alert.id}
                  className={`p-4 rounded-lg border ${alert.severity === 'high'
                      ? 'border-red-200 bg-red-50'
                      : alert.severity === 'medium'
                        ? 'border-yellow-200 bg-yellow-50'
                        : 'border-blue-200 bg-blue-50'
                    }`}
                >
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center">
                      {alert.severity === 'high' ? (
                        <AlertTriangle className="mr-2 h-5 w-5 text-red-500" />
                      ) : alert.severity === 'medium' ? (
                        <AlertTriangle className="mr-2 h-5 w-5 text-yellow-500" />
                      ) : (
                        <Info className="mr-2 h-5 w-5 text-blue-500" />
                      )}
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${alert.severity === 'high'
                          ? 'bg-red-100 text-red-800'
                          : alert.severity === 'medium'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                        {alert.severity.charAt(0).toUpperCase() + alert.severity.slice(1)}
                      </span>
                    </div>
                    <span className="text-xs text-gray-500">{alert.timestamp}</span>
                  </div>
                  <p className="text-sm text-gray-700 mb-2">{alert.description}</p>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500 capitalize">
                      {alert.type.replace('_', ' ')}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${alert.status === 'new'
                        ? 'bg-red-100 text-red-800'
                        : alert.status === 'investigated'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-green-100 text-green-800'
                      }`}>
                      {alert.status.charAt(0).toUpperCase() + alert.status.slice(1)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RiskManagement;